/**
 * RecipeBook — Visual catalog of all crafting and furnace recipes.
 * Toggle with R key or from crafting screen.
 * Shows recipe patterns with item icons.
 */

import { renderBlockIcon, renderItemIcon, BLOCK_NAMES } from './Hotbar.js';
import { getItemName } from '../items/ItemRegistry.js';
import { isTool, getToolName } from '../items/ToolSystem.js';

const SLOT_SIZE = 36;
const RECIPES_PER_PAGE = 6;

export class RecipeBook {
  constructor(craftingSystem) {
    this.craftingSystem = craftingSystem;
    this.isOpen = false;
    this._page = 0;
    this._tab = 'crafting'; // 'crafting' or 'furnace'
    this._build();
  }

  _build() {
    this.overlay = document.createElement('div');
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55); z-index: 500;
      display: none; justify-content: center; align-items: center;
      cursor: default;
    `;

    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #C6C6C6;
      border: 4px solid #2A2A2A;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      padding: 10px;
      font-family: 'Courier New', monospace; color: #404040;
      user-select: none; image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #8B8B8B;
      max-height: 80vh;
      overflow-y: auto;
      min-width: 420px;
    `;

    // Title
    const title = document.createElement('div');
    title.textContent = 'Libro de Recetas';
    title.style.cssText = 'font-size: 16px; font-weight: bold; margin-bottom: 8px; color: #404040; text-align: center;';
    this.panel.appendChild(title);

    // Tabs
    this.tabBar = document.createElement('div');
    this.tabBar.style.cssText = 'display: flex; gap: 4px; margin-bottom: 8px;';

    this.craftingTab = this._createTab('Crafteo', () => { this._tab = 'crafting'; this._page = 0; this._render(); });
    this.furnaceTab = this._createTab('Fundición', () => { this._tab = 'furnace'; this._page = 0; this._render(); });

    this.tabBar.appendChild(this.craftingTab);
    this.tabBar.appendChild(this.furnaceTab);
    this.panel.appendChild(this.tabBar);

    // Recipe list container
    this.recipeList = document.createElement('div');
    this.recipeList.style.cssText = 'min-height: 300px;';
    this.panel.appendChild(this.recipeList);

    // Pagination
    this.pagination = document.createElement('div');
    this.pagination.style.cssText = 'display: flex; justify-content: center; align-items: center; gap: 10px; margin-top: 8px;';

    this.prevBtn = document.createElement('button');
    this.prevBtn.textContent = '< Anterior';
    this.prevBtn.style.cssText = this._buttonStyle();
    this.prevBtn.addEventListener('click', () => { if (this._page > 0) { this._page--; this._render(); } });

    this.pageLabel = document.createElement('span');
    this.pageLabel.style.cssText = 'font-size: 12px; color: #404040;';

    this.nextBtn = document.createElement('button');
    this.nextBtn.textContent = 'Siguiente >';
    this.nextBtn.style.cssText = this._buttonStyle();
    this.nextBtn.addEventListener('click', () => { this._page++; this._render(); });

    this.pagination.appendChild(this.prevBtn);
    this.pagination.appendChild(this.pageLabel);
    this.pagination.appendChild(this.nextBtn);
    this.panel.appendChild(this.pagination);

    this.overlay.appendChild(this.panel);
    document.body.appendChild(this.overlay);
  }

  _createTab(label, onClick) {
    const tab = document.createElement('div');
    tab.textContent = label;
    tab.style.cssText = `
      padding: 4px 12px; font-size: 12px; cursor: pointer;
      background: #8B8B8B; color: #FFF; border: 2px solid #555;
      border-radius: 2px;
    `;
    tab.addEventListener('click', onClick);
    return tab;
  }

  _buttonStyle() {
    return `
      padding: 3px 10px; font-size: 11px; cursor: pointer;
      background: #8B8B8B; color: #FFF; border: 2px solid #555;
      border-radius: 2px; font-family: 'Courier New', monospace;
    `;
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.overlay.style.display = this.isOpen ? 'flex' : 'none';
    if (this.isOpen) this._render();
  }

  close() {
    this.isOpen = false;
    this.overlay.style.display = 'none';
  }

  _render() {
    // Update tab styles
    this.craftingTab.style.background = this._tab === 'crafting' ? '#5A5A5A' : '#8B8B8B';
    this.furnaceTab.style.background = this._tab === 'furnace' ? '#5A5A5A' : '#8B8B8B';

    const recipes = this._tab === 'crafting'
      ? this.craftingSystem.getAllRecipes()
      : this.craftingSystem.getAllFurnaceRecipes();

    const totalPages = Math.ceil(recipes.length / RECIPES_PER_PAGE);
    if (this._page >= totalPages) this._page = Math.max(0, totalPages - 1);

    const start = this._page * RECIPES_PER_PAGE;
    const pageRecipes = recipes.slice(start, start + RECIPES_PER_PAGE);

    this.recipeList.innerHTML = '';

    for (const recipe of pageRecipes) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 6px;
        padding: 6px; margin-bottom: 4px;
        background: rgba(0,0,0,0.06); border-radius: 3px;
      `;

      if (this._tab === 'crafting') {
        this._renderCraftingRecipe(row, recipe);
      } else {
        this._renderFurnaceRecipe(row, recipe);
      }

      this.recipeList.appendChild(row);
    }

    // Pagination
    this.prevBtn.style.display = this._page > 0 ? 'block' : 'none';
    this.nextBtn.style.display = this._page < totalPages - 1 ? 'block' : 'none';
    this.pageLabel.textContent = `${this._page + 1} / ${totalPages}`;
  }

  _renderCraftingRecipe(row, recipe) {
    // Pattern grid
    const gridContainer = document.createElement('div');
    const patternH = recipe.pattern.length;
    const patternW = Math.max(...recipe.pattern.map(r => r.length));

    gridContainer.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${patternW}, ${SLOT_SIZE}px);
      gap: 1px;
    `;

    for (let r = 0; r < patternH; r++) {
      for (let c = 0; c < patternW; c++) {
        const ch = (recipe.pattern[r] || '')[c] || ' ';
        const slot = this._createMiniSlot();

        if (ch !== ' ' && recipe.key[ch]) {
          const ingredient = recipe.key[ch];
          if (ingredient.blockId !== undefined) {
            slot.innerHTML = renderBlockIcon(ingredient.blockId, SLOT_SIZE - 8);
          } else if (ingredient.itemId !== undefined) {
            slot.innerHTML = renderItemIcon(ingredient.itemId, SLOT_SIZE - 8);
          }
        }

        gridContainer.appendChild(slot);
      }
    }

    row.appendChild(gridContainer);

    // Arrow
    const arrow = document.createElement('div');
    arrow.textContent = '\u27A1';
    arrow.style.cssText = 'font-size: 20px; color: #666; margin: 0 6px;';
    row.appendChild(arrow);

    // Result
    const resultSlot = this._createMiniSlot();
    resultSlot.style.borderColor = '#FFD700';
    const result = recipe.result;

    if (result.toolType) {
      resultSlot.innerHTML = `<div style="font-size: 10px; text-align: center; color: #333;">
        ${result.material}<br>${result.toolType}
      </div>`;
    } else if (result.blockId !== undefined) {
      resultSlot.innerHTML = renderBlockIcon(result.blockId, SLOT_SIZE - 8);
    } else if (result.itemId !== undefined) {
      resultSlot.innerHTML = renderItemIcon(result.itemId, SLOT_SIZE - 8);
    }

    row.appendChild(resultSlot);

    // Count label
    const count = result.count || 1;
    if (count > 1) {
      const countLabel = document.createElement('div');
      countLabel.textContent = `x${count}`;
      countLabel.style.cssText = 'font-size: 12px; font-weight: bold; color: #404040; margin-left: 2px;';
      row.appendChild(countLabel);
    }

    // Result name
    const nameLabel = document.createElement('div');
    nameLabel.style.cssText = 'font-size: 10px; color: #606060; margin-left: 6px; max-width: 80px;';
    nameLabel.textContent = this._getResultName(result);
    row.appendChild(nameLabel);
  }

  _renderFurnaceRecipe(row, recipe) {
    // Input slot
    const inputSlot = this._createMiniSlot();
    const input = recipe.input;
    if (input.blockId !== undefined) {
      inputSlot.innerHTML = renderBlockIcon(input.blockId, SLOT_SIZE - 8);
    } else if (input.itemId !== undefined) {
      inputSlot.innerHTML = renderItemIcon(input.itemId, SLOT_SIZE - 8);
    }
    row.appendChild(inputSlot);

    // Fire icon
    const fire = document.createElement('div');
    fire.textContent = '\u{1F525}';
    fire.style.cssText = 'font-size: 18px; margin: 0 6px;';
    row.appendChild(fire);

    // Arrow
    const arrow = document.createElement('div');
    arrow.textContent = '\u27A1';
    arrow.style.cssText = 'font-size: 20px; color: #666; margin: 0 6px;';
    row.appendChild(arrow);

    // Output slot
    const outputSlot = this._createMiniSlot();
    outputSlot.style.borderColor = '#FFD700';
    const result = recipe.result;
    if (result.blockId !== undefined) {
      outputSlot.innerHTML = renderBlockIcon(result.blockId, SLOT_SIZE - 8);
    } else if (result.itemId !== undefined) {
      outputSlot.innerHTML = renderItemIcon(result.itemId, SLOT_SIZE - 8);
    }
    row.appendChild(outputSlot);

    // Name
    const nameLabel = document.createElement('div');
    nameLabel.style.cssText = 'font-size: 11px; color: #606060; margin-left: 8px;';
    nameLabel.textContent = this._getResultName(result);
    row.appendChild(nameLabel);
  }

  _createMiniSlot() {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      background: #8B8B8B;
      border: 2px solid #555;
      display: flex; align-items: center; justify-content: center;
      position: relative;
    `;
    return slot;
  }

  _getResultName(result) {
    if (result.toolType) {
      const mats = { wood: 'Madera', stone: 'Piedra', iron: 'Hierro', gold: 'Oro', diamond: 'Diamante' };
      const types = { pickaxe: 'Pico', shovel: 'Pala', axe: 'Hacha', sword: 'Espada', hoe: 'Azadón' };
      return `${types[result.toolType]} de ${mats[result.material]}`;
    }
    if (result.blockId !== undefined) {
      return BLOCK_NAMES[result.blockId] || `Bloque ${result.blockId}`;
    }
    if (result.itemId !== undefined) {
      return getItemName(result.itemId) || `Item ${result.itemId}`;
    }
    return '???';
  }
}
